import { useEffect, useState } from "react";
import { Feature, Polygon, Properties, area } from "@turf/turf";
import { Layer, Map, GeoJSON } from "leaflet";
import { floor } from "lodash";

import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { ConfigParameters } from "types/types";

import {
  convertLatLngsToPolygon,
  getFlandersStudyAreaIntersection,
} from "utils/DrawUtils";
import { latLonToLonLat } from "utils/TurfHelperUtils";
import { createDivWithId } from "utils/htmlUtils";

interface Props {
  mapRef: Map;
  color: string;
  fillColor: string;
  configParameters: ConfigParameters;
  geoJSONLayer: Layer | GeoJSON | null;
  addGeoJSONLayer: (geoJsonLayer: Layer | GeoJSON | null) => void;
}

export function DrawStudyArea(props: Props) {
  const {
    mapRef,
    color,
    fillColor,
    configParameters,
    geoJSONLayer,
    addGeoJSONLayer,
  } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [rerender, setRerender] = useState(false);

  useEffect(() => {
    function cleanUp() {
      setRerender((prev) => !prev);
      mapRef.pm.disableDraw("Polygon");
      mapRef.off("pm:create");
      mapRef.off("pm:vertexadded");
      mapRef.off("pm:drawstart");
    }

    // @ts-ignore
    if (mapRef.pm.Draw.Polygon._enabled) return;
    mapRef.on("pm:drawstart", (e) => {
      e.workingLayer.on("pm:change", (ev) => {
        const polygonLatLngs = ev.latlngs;
        const tooltipPane = mapRef.getPanes().tooltipPane;
        // @ts-ignore
        const [firstTooltip] = tooltipPane.getElementsByClassName(
          "leaflet-tooltip-bottom"
        );
        const polygon = convertLatLngsToPolygon(polygonLatLngs);

        if (!polygon) return;

        function updateDivContent(
          div: HTMLElement,
          polygon: Feature<Polygon, Properties>
        ) {
          div.innerHTML = `${t("label.areaSqM", {
            area: floor(area(polygon), 2).toLocaleString("nl-BE"),
          })}`;
        }
        const squareMeterDiv =
          document.getElementById("squareMeterDiv") ||
          createDivWithId("squareMeterDiv");

        updateDivContent(squareMeterDiv, polygon);

        if (!document.getElementById("squareMeterDiv")) {
          firstTooltip.appendChild(squareMeterDiv);
        }
      });
    });

    mapRef.pm.enableDraw("Polygon", {
      finishOn: "dblclick",
      allowSelfIntersection: false,
      templineStyle: {
        color: color,
      },
      hintlineStyle: {
        color: color,
      },
      pathOptions: {
        color: color,
        fillColor: fillColor,
        fillOpacity: 0,
      },
    });

    mapRef.on("pm:create", (event) => {
      const { layer } = event;
      const { studyAreaMin_m2, studyAreaMax_m2, domain_4326 } =
        configParameters;
      const intersection = getFlandersStudyAreaIntersection(domain_4326, layer);
      if (!intersection) {
        enqueueSnackbar(t("label.studyAreaShouldBeInFlanders"), {
          variant: "error",
        });
        layer.remove();
        setRerender((prev) => !prev);
        return cleanUp();
      }
      const surfaceArea = area(intersection);
      if (surfaceArea < studyAreaMin_m2 || surfaceArea > studyAreaMax_m2) {
        enqueueSnackbar(
          t("label.studyAreaShouldBeBetween", {
            min: studyAreaMin_m2,
            max: studyAreaMax_m2.toLocaleString("nl-BE"),
          }),
          {
            variant: "error",
          }
        );
        layer.remove();
        // setRerender((prev) => !prev);
        return cleanUp();
      }
      //@ts-ignore
      layer.setLatLngs(latLonToLonLat(intersection.geometry.coordinates));
      addGeoJSONLayer(layer);
      return cleanUp();
    });
  }, [
    mapRef,
    color,
    fillColor,
    configParameters,
    geoJSONLayer,
    addGeoJSONLayer,
    t,
    enqueueSnackbar,
    rerender,
  ]);

  useEffect(() => {
    return () => {
      mapRef.pm.disableDraw("Polygon");
      mapRef.off("pm:create");
      mapRef.off("pm:vertexadded");
      mapRef.off("pm:drawstart");
    };
  }, [mapRef]);

  return null;
}
